import React, { createContext, useEffect, useState } from 'react';
import { Button, Card, InputNumber, Select, Space, Tabs, message } from 'antd';
import useService from '@/lib/hooks/useService';
import AddOrEditModal from '../AddOrEditModal';
import OrdersList from '../OrdersList';
import PublishAnnouncement from '../PublishAnnouncement';
import { GroupBuyingOrdersContextDefaultValue } from '../../GroupBuyingOrdersContextDefaultValue';
import styles from './index.module.scss';
import type { CommunityData } from '@/types/database/communityData';
import type { GroupBuying } from '@/types/database/groupBuying';

interface GroupBuyingOrdersContextValue {
  groupBuyingOrders: GroupBuying[];
  updateGroupBuyingOrder: () => void;
}

const selectOptions = [
  { label: '天', value: '天' },
  { label: '週', value: '週' },
  { label: '月', value: '月' },
];

export const GroupBuyingOrdersContext =
  createContext<GroupBuyingOrdersContextValue>({
    groupBuyingOrders: GroupBuyingOrdersContextDefaultValue,
    updateGroupBuyingOrder: () => {},
  });

const SettingAndManagement = () => {
  const [groupBuyingOrders, updateGroupBuyingOrder] = useService<GroupBuying>({
    collectionName: 'groupBuying',
  });
  const [communityData, updateCommunityData, communityDataService] =
    useService<CommunityData>({
      collectionName: 'communityData',
    });

  const [isClosingOrderDisabled, setIsClosingOrderDisabled] = useState(true);
  const [closingOrderNumber, setClosingOrderNumber] = useState(0);
  const [closingOrderUnit, setClosingOrderUnit] = useState<'天' | '週' | '月'>(
    '天'
  );

  const tabsItems = [
    {
      label: '現在團購',
      key: '現在團購',
      children: <OrdersList listType="現在團購" />,
    },
    {
      label: '歷史資料',
      key: '歷史資料',
      children: <OrdersList listType="歷史資料" />,
    },
  ];

  useEffect(() => {
    if (communityData[0]?.groupBuying) {
      setClosingOrderNumber(communityData[0].groupBuying.closingOrderNumber);
      setClosingOrderUnit(communityData[0].groupBuying.closingOrderUnit);
    }
  }, [communityData]);

  const handleClosingOrderNumberChange = async (value: number) => {
    setClosingOrderNumber(value);
  };

  const handleClosingOrderUnitChange = async (value: '天' | '週' | '月') => {
    setClosingOrderUnit(value);
  };

  const changeClosingOrderValues = async () => {
    setIsClosingOrderDisabled(true);
    await communityDataService.patch(communityData[0]._id, {
      groupBuying: {
        closingOrderNumber,
        closingOrderUnit,
      },
    });
    updateCommunityData();
    message.success('資料更新成功!');
  };

  const GroupBuyingOrdersContextValues = {
    groupBuyingOrders,
    updateGroupBuyingOrder,
  };

  const propsForAnnouncement = {
    closingOrderNumber,
    closingOrderUnit,
  };

  return (
    <GroupBuyingOrdersContext.Provider value={GroupBuyingOrdersContextValues}>
      <Space size="middle" direction="vertical" className={styles.container}>
        <Space direction="vertical" className={styles.container}>
          <AddOrEditModal />
          <Card
            actions={[
              <div
                key="announcementButtons"
                className={styles.announcementButtonsContainer}
              >
                <Space>
                  <PublishAnnouncement
                    disabled={!isClosingOrderDisabled}
                    propsForAnnouncement={propsForAnnouncement}
                  />
                  {isClosingOrderDisabled ? (
                    <Button onClick={() => setIsClosingOrderDisabled(false)}>
                      編輯
                    </Button>
                  ) : (
                    <Button onClick={changeClosingOrderValues}>儲存</Button>
                  )}
                </Space>
              </div>,
            ]}
          >
            <h2>團購訂單管理辦法</h2>
            <Space align="baseline">
              <span>1.住戶最晚可於團購結單時間前</span>
              <Space>
                <InputNumber
                  placeholder="請填入數字"
                  value={closingOrderNumber}
                  min={1}
                  disabled={isClosingOrderDisabled}
                  className={styles.input}
                  onChange={handleClosingOrderNumberChange}
                />
                <Select
                  className={styles.selector}
                  options={selectOptions}
                  disabled={isClosingOrderDisabled}
                  value={closingOrderUnit}
                  onChange={handleClosingOrderUnitChange}
                />
              </Space>
              <span>取消預約，超過時間則不可取消預約。(含住戶推播通知)</span>
            </Space>
          </Card>
        </Space>
        <Tabs items={tabsItems} />
      </Space>
    </GroupBuyingOrdersContext.Provider>
  );
};

export default SettingAndManagement;
